import { Component, OnInit, OnDestroy, HostListener, ElementRef, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { ScrollToTop } from '../../components/scroll-to-top/scroll-to-top';
import { EventCard } from '../../components/event-card/event-card';
import { Event, EventsService } from '../../core/services/events.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';

@Component({
  selector: 'app-home',
  imports: [
    CommonModule,
    FormsModule,
    Header,
    Footer,
    ScrollToTop,
    EventCard
  ],
  templateUrl: './home.html',
  styleUrl: './home.css'
})
export class Home implements OnInit, OnDestroy {
  // General
  allEvents: Event[] = [];

  // Banner
  featuredEventsForBanner: Event[] = [];
  currentSlide = 0;
  private slideInterval: any;

  // Search
  searchQuery = '';
  filteredEvents: Event[] = [];
  showResults = false;
  searchPerformed = false;
  private searchTimeout: any;

  // Featured Events Section
  featuredEvents: Event[] = [];
  nonFeaturedEvents: Event[] = [];

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private eventsService: EventsService,
    private webAppConfigService: WebAppConfigService,
    private router: Router,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    // SIEMPRE cargar config primero (APP_INITIALIZER puede estar en progreso)
    this.webAppConfigService.loadConfig().subscribe(() => {
      this.webAppConfigService.applyHomeMetadata();
    });
    this.loadEvents();
  }

  ngOnDestroy(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }

  loadEvents(): void {
    this.eventsService.getPublicEvents().subscribe({
      next: (response) => {
        if (response.success) {
          const eventosWeb = response.data.filter((event) => {
            const canalWeb = (event?.canales || []).find((canal: any) => canal?.canal === 'web');
            if (!canalWeb) {
              return false;
            }
            const canalWebLegacy = canalWeb as any;

            const disponible =
              canalWeb?.disponible === true ||
              canalWebLegacy?.disponibilidad === 'disponible';

            const publicado =
              canalWeb?.publicado === true ||
              canalWebLegacy?.estado === 'publicado';

            return disponible && publicado;
          });

          this.allEvents = [...eventosWeb];
          this.filterFeaturedEvents();
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('❌ Error loading events:', error);
      }
    });
  }

  // Banner methods
  filterFeaturedEvents(): void {
    this.featuredEventsForBanner = [...this.allEvents.filter(event => event.destacado === true)];
    this.featuredEvents = [...this.allEvents.filter(event => event.destacado === true).slice(0, 4)];
    this.nonFeaturedEvents = [...this.allEvents.filter(event => event.destacado !== true)];
    
    // Reiniciar el slider
    this.currentSlide = 0;
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
    
    if (this.featuredEventsForBanner.length > 1 && typeof window !== 'undefined') {
      this.startAutoSlide();
    }
  }

  getEventBannerImage(event: Event): string {
    return this.eventsService.getEventImage(event, 'banner');
  }

  startAutoSlide(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
    
    this.slideInterval = setInterval(() => {
      this.nextSlide();
    }, 5000);
  }

  nextSlide(): void {
    if (this.featuredEventsForBanner.length > 0) {
      this.currentSlide = (this.currentSlide + 1) % this.featuredEventsForBanner.length;
    }
  }

  prevSlide(): void {
    if (this.featuredEventsForBanner.length > 0) {
      this.currentSlide = this.currentSlide === 0 
        ? this.featuredEventsForBanner.length - 1 
        : this.currentSlide - 1;
    }
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.startAutoSlide();
    }
  }

  // Search methods
  onSearchInput(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const trimmedQuery = (this.searchQuery || '').trim();

    if (trimmedQuery.length === 0) {
      this.filteredEvents = [];
      this.showResults = false;
      this.searchPerformed = false;
      return;
    }

    this.performSearch(this.searchQuery);
  }

  performSearch(query: string): void {
    const trimmedQuery = (query || '').trim();

    if (trimmedQuery.length === 0) {
      this.filteredEvents = [];
      this.showResults = false;
      this.searchPerformed = false;
      return;
    }

    const searchTerm = trimmedQuery.toLowerCase();

    this.filteredEvents = this.allEvents.filter(event => {
      const matchesName = event.nombre.toLowerCase().includes(searchTerm);
      const matchesCategory = event.categoria?.toLowerCase().includes(searchTerm);
      const matchesLocation = event.lugar?.toLowerCase().includes(searchTerm);

      return matchesName || matchesCategory || matchesLocation;
    }).slice(0, 8);

    this.showResults = this.filteredEvents.length > 0;
    this.searchPerformed = true;
  }

  getEventImage(event: Event): string {
    return this.eventsService.getEventImage(event, 'cuadrada');
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredEvents = [];
    this.showResults = false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(clickEvent: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(clickEvent.target as Node)) {
      this.showResults = false;
    }
  }

  // Navigation
  navigateToEvent(event: Event): void {
    this.router.navigate(['/evento', event.url_slug]);
  }

  // Date formatting
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
}
